import { Button, Card, Stack, Text, Group, Divider } from "@mantine/core";
import type { MouseEvent as ReactMouseEvent } from "react";
import { IconExternalLink, IconChevronRight } from "@tabler/icons-react";
import useTransientStore from "../../../data/transcientStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { ReportView } from "./reportsLandingData";
import { useReportsPageAccess } from "../useReportsPageAccess";

interface ReportsLandingProps {
  onNavigate?: (view: ReportView) => void;
}

export default function ReportsLanding({ onNavigate }: ReportsLandingProps) {
  const selectedAddressSystemId =
    useTransientStore((state) => state.selectedAddressSystem?.id) ?? 0;
  const { getTranslation } = useTranslation({
    pageId: 512,
    addressSystemId: selectedAddressSystemId,
    autoLoad: true,
  });
  const { visibleLinks } = useReportsPageAccess();
  const powerBiLink = visibleLinks.find(
    (link) => link.id === "open-powerbi-summary-report",
  );
  const reportPageLinks = visibleLinks.filter(
    (link) => link.id !== "open-powerbi-summary-report",
  );

  return (
    <Card withBorder radius="lg" px="xl" py="lg" shadow="sm">
      <Stack gap="md">
        {powerBiLink && (
          <Button
            component="a"
            href={powerBiLink.href}
            target="_blank"
            rel="noreferrer"
            variant="subtle"
            color="blue"
            justify="space-between"
            rightSection={<IconExternalLink size={16} />}
            styles={{
              root: {
                height: "auto",
                padding: "8px 10px",
              },
              inner: {
                justifyContent: "space-between",
              },
              label: {
                width: "100%",
              },
            }}
          >
            <Group gap="xs" wrap="nowrap" justify="space-between">
              <Text size="md" fw={600} c="blue">
                {getTranslation(powerBiLink.translationKey) ||
                  powerBiLink.defaultLabel}
              </Text>
            </Group>
          </Button>
        )}
        {powerBiLink && reportPageLinks.length > 0 && <Divider variant="dashed" />}
        <Stack gap="xs">
          {reportPageLinks.map((link) => {
            const label =
              getTranslation(link.translationKey) || link.defaultLabel;
            return (
              <Button
                key={link.id}
                component={link.external ? "a" : "button"}
                href={link.external ? link.href : undefined}
                target={link.external ? "_blank" : undefined}
                rel={link.external ? "noreferrer" : undefined}
                variant="subtle"
                color="blue"
                justify="space-between"
                rightSection={
                  link.external ? (
                    <IconExternalLink size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )
                }
                onClick={(
                  event: ReactMouseEvent<HTMLButtonElement | HTMLAnchorElement>,
                ) => {
                  if (link.navigationTarget && onNavigate) {
                    event.preventDefault();
                    onNavigate(link.navigationTarget);
                  }
                }}
                styles={{
                  root: {
                    height: "auto",
                    padding: "8px 10px",
                  },
                  inner: {
                    justifyContent: "space-between",
                  },
                  label: {
                    width: "100%",
                  },
                }}
              >
                <Group gap="xs" wrap="nowrap" justify="space-between">
                  <Text size="md" fw={600} c="blue">
                    {label}
                  </Text>
                </Group>
              </Button>
            );
          })}
        </Stack>
      </Stack>
    </Card>
  );
}
